import dayjs from "dayjs";
import { ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import {
  MemberFollowup,
  FollowUpStatus,
  parseFollowUpStatus,
} from "../../model/members/MemberFollowup";
import { SaveMemberFollowUpRequest } from "../../requests/member/SaveMemberFollowUpRequest";

function coerceToDateOnly(value: unknown, fallback: dayjs.Dayjs): string {
  if (value instanceof Date) {
    // Format from the LOCAL calendar fields, not toISOString() — the latter
    // converts to UTC and rolls the day back in UTC-ahead zones (see toDateOnly
    // in utils/DateOnly).
    return dayjs(value).format("YYYY-MM-DD");
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return fallback.format("YYYY-MM-DD");
    }
    const parsed = dayjs(trimmed);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD");
    }
    const jsDate = new Date(trimmed);
    if (!Number.isNaN(jsDate.getTime())) {
      return dayjs(jsDate).format("YYYY-MM-DD");
    }
    return fallback.format("YYYY-MM-DD");
  }

  return fallback.format("YYYY-MM-DD");
}

function normalizeFollowupFromApi(raw: Record<string, unknown>): MemberFollowup {
  const createdAtRaw = raw.createdAt as unknown;
  const updatedAtRaw = raw.updatedAt as unknown;

  return {
    id: raw.id as number | undefined,
    membershipId: String(raw.membershipId ?? ""),
    notes: String(raw.notes ?? ""),
    followUpDate: String(raw.followUpDate ?? ""),
    nextFollowUpDate:
      raw.nextFollowUpDate != null ? String(raw.nextFollowUpDate) : undefined,
    status: parseFollowUpStatus(raw.status),
    clientName:
      raw.clientName != null ? String(raw.clientName) : undefined,
    contactedByStaffId:
      raw.contactedByStaffId != null ? Number(raw.contactedByStaffId) : null,
    contactedByStaffName:
      raw.contactedByStaffName != null
        ? String(raw.contactedByStaffName)
        : undefined,
    createdAt:
      createdAtRaw instanceof Date
        ? createdAtRaw.toISOString()
        : createdAtRaw != null
        ? String(createdAtRaw)
        : undefined,
    updatedAt:
      updatedAtRaw instanceof Date
        ? updatedAtRaw.toISOString()
        : updatedAtRaw != null
        ? String(updatedAtRaw)
        : undefined,
  };
}

function followUpStatusToString(status: FollowUpStatus): string {
  return FollowUpStatus[status] ?? String(status);
}

function toSaveRequest(
  f: MemberFollowup,
  memberId: string,
  clientName: string
): SaveMemberFollowUpRequest {
  const today = dayjs();
  const followUpDate = coerceToDateOnly(f.followUpDate, today);
  const nextFollowUpDate = coerceToDateOnly(
    f.nextFollowUpDate ?? f.followUpDate ?? followUpDate,
    today
  );

  return {
    membershipId: f.membershipId || memberId,
    notes: f.notes ?? "",
    followUpDate,
    nextFollowUpDate,
    status: followUpStatusToString(f.status),
    clientName: f.clientName ?? clientName,
    contactedByStaffId: f.contactedByStaffId ?? null,
  };
}

export class MemberFollowupService extends ClubBookingApiClient {
  async getFollowups(): Promise<MemberFollowup[]> {
    try {
      const raw = await this.get<Record<string, unknown>[]>("/followups/member");
      return Array.isArray(raw) ? raw.map(normalizeFollowupFromApi) : [];
    } catch (error) {
      console.error("Error getting follow-ups:", error);
      throw error;
    }
  }

  async saveFollowup(
    memberId: string,
    followup: MemberFollowup,
    clientName: string
  ): Promise<void> {
    try {
      const body = toSaveRequest(followup, memberId, clientName);
      await this.post<void>(`/followups/member/${memberId}`, body, {
        successMessage: "Follow-up saved successfully",
        showLoading: true,
      });
    } catch (error) {
      console.error("Error saving follow-up:", error);
      throw error;
    }
  }

  async saveFollowups(
    memberId: string,
    followups: MemberFollowup[],
    clientName: string
  ): Promise<void> {
    try {
      const body: SaveMemberFollowUpRequest[] = followups.map((f) =>
        toSaveRequest(f, memberId, clientName)
      );
      await this.post<void>(`/followups/member/${memberId}`, body, {
        successMessage: "Follow-ups saved successfully",
        showLoading: true,
      });
    } catch (error) {
      console.error("Error saving follow-ups:", error);
      throw error;
    }
  }
}
